import type { HTMLAttributes } from 'react';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { IconButton } from '../buttons';
import { Notification } from '../notification';
import { Paragraph } from '../typography';

import styles from './copy-to-clipboard.module.css';

export type CopyToClipboardProps = {
  value?: string;
  label?: string;
  buttonPosition?: 'right' | 'left';
  notificationPosition?: 'right' | 'left' | 'above' | 'below';
} & HTMLAttributes<HTMLDivElement>;

/**
 * @param {string} [label] Label to be display next to the copy button
 * @default undefined
 * @param {string} [value] Value to by copied
 * @default ''
 * @param {'right' | 'left'} [buttonPosition] Position of the copy button relative to label
 * @default 'left'
 * @param {'right' | 'left' | 'above' | 'below'} [notificationPosition] Position of the notification relative to copy button
 * @default 'right'
 */
export function CopyToClipboard({
  label,
  value = '',
  buttonPosition = 'left',
  notificationPosition = 'right',
  ...props
}: CopyToClipboardProps) {
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  const onCopyToClipboard = () => {
    window.navigator.clipboard
      .writeText(value)
      .then(() => {
        setError(false);
        setIsNotificationVisible(true);
      })
      .catch(() => {
        setError(true);
        setIsNotificationVisible(true);
      });
  };

  const handleAnimationEnd = () => {
    setIsNotificationVisible(false);
  };

  return (
    <div
      className={classNames(styles.wrapper, buttonPosition === 'right' && label && styles.reverse)}
      data-testid="copy-to-clipboard"
      {...props}
    >
      <IconButton
        rounded
        data-testid="copy-to-clipboard-button"
        type="button"
        aria-label={t('copyToClipboard.button.label')}
        onClick={onCopyToClipboard}
      >
        <CopyIcon />
      </IconButton>

      {label && <Paragraph>{label}</Paragraph>}

      {isNotificationVisible && (
        <Notification
          status={error ? 'error' : 'success'}
          text={error ? t('copyToClipboard.notification.error') : t('copyToClipboard.notification.success')}
          className={classNames(styles.notification, styles[notificationPosition])}
          onAnimationEnd={handleAnimationEnd}
        />
      )}
    </div>
  );
}
